import React, { Fragment, ReactNode, useEffect, useState } from "react";
import { Alert, Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { getAccountUsage, getBillingOrder, UsageTerm } from "../ApiUsagePage";
import { currentUser, InfegyAccount } from "system/InfegyUser";
import { TextLoader } from "components/apiHandling/TextLoader";
import { ApiPricingModel, BillingOrder } from "../types/BillingOrder";
import { Usage, UsageCategoryKey } from "../types/Usage";
import ISONumber from "components/formatting/ISONumber";
import AbbreviateNumber from "components/formatting/AbbreviateNumber";
import DateRangeLabel from "../components/DateRangeLabel";

interface PayAsYouGoUsageProps {
  account: InfegyAccount;
  currentTerm: UsageTerm;
  visible: boolean;
}

interface PaygBucket {
  pricing: ApiPricingModel;
  usage: number;
}

type PaygBillingModel = {
  [Property in UsageCategoryKey]?: PaygBucket
}

export default function PayAsYouGoUsage(props: PayAsYouGoUsageProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const thisUser = currentUser();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string>("");
  const [paygBillingModel, setPaygBillingModel] = useState<PaygBillingModel | null>(null);
  const [paygUsage, setPaygUsage] = useState<Usage | null>(null);
  
  function calculatePaygTotalUsage(subscriptionUsage: Usage, billingOrder: BillingOrder, paygUsage: Usage): PaygBillingModel {
    const paygBillingModel: PaygBillingModel = {};
    let pricing: ApiPricingModel;

    // Loop through all categories that have usage.
    for (const key in subscriptionUsage.total.cost) {
      const usageCategory = key as UsageCategoryKey;

      // Determine the initial allotment.
      pricing = billingOrder.data.api_pricing[usageCategory];
      const allotment = pricing.allotment.count;

      const subscriptionCount = subscriptionUsage.total.cost[usageCategory] || 0;
      if (allotment > subscriptionCount) {
        // Usage for this category is covered by the allotment.  There will be no PaYG usage for this category.
        continue;
      }

      // Find out how much of the allotment is left after deducting usage from the previous term(s).
      let allotmentRemaining = allotment;
      for (const day of subscriptionUsage.timeline) {
        if (allotmentRemaining <= 0) {
          allotmentRemaining = 0;
          break;
        }
        const timelineDate = new Date(day.date);
        const paygUsageStart = new Date(paygUsage.since);
        if (timelineDate >= paygUsageStart) {
          break;
        }
        allotmentRemaining -= day[`${usageCategory}_cost`] || 0;
      }
      
      // Apply the remaining allotment to the current usage term, if applicable.  Then find out if there is PaYG usage.
      const termCount = paygUsage.total.cost[usageCategory] || 0
      const paygCount = Math.max(termCount - allotmentRemaining, 0);

      paygBillingModel[usageCategory] = {
        pricing,
        usage: paygCount
      };
    }

    return paygBillingModel;
  }

  async function load() {
    setIsLoading(true);
    setPaygUsage(null);
    setLoadError("");
    setPaygBillingModel(null);

    try {
      const subscriptionUsage = await getAccountUsage(props.currentTerm, props.account.id);
      
      let billingOrder: BillingOrder | null = null;
      if (props.account.billing_order_id) {
        billingOrder = await getBillingOrder(props.account.billing_order_id, !!thisUser?.admin);
      } else {
        setIsLoading(false);
        return;
      }

      let termStart = props.account.api_payg_billed_to;
      if (!termStart) {
        termStart = billingOrder.data.subscription_start_date;
      }
      const paygTerm: UsageTerm = {
        term_start: termStart
      }

      let paygUsage: Usage = await getAccountUsage(paygTerm, props.account.id);

      const paygBillingModel: PaygBillingModel = calculatePaygTotalUsage(subscriptionUsage, billingOrder, paygUsage);

      setPaygUsage(paygUsage);
      setPaygBillingModel(paygBillingModel);
    } catch (error: any) {
      if (error.code) {
        // This was an error from the API.  Display it as such.
        setLoadError(`${error.message}: ${error.server_message}`);
      } else {
        // This was an error from the UI.
        setLoadError(`${i18n.t("api-usage-page.error-loading")} ${error}`);
      }
    }

    setIsLoading(false);
  }

  useEffect(() => {
    load();
  }, [props.currentTerm, props.account]);

  const rows: Array<ReactNode> = [];
  let sumPrice = 0;
  if (paygUsage && paygBillingModel) {

    for (const entry of Object.entries(paygBillingModel)) {
      const paygBucket = entry[1];
      const name = paygBucket.pricing.name;
      const description = paygBucket.pricing.description;
      const usage = paygBucket.usage;
      const unitPrice = paygBucket.pricing.price;
      const totalCost = Math.max(0, usage * unitPrice);
      sumPrice += Math.max(0, usage * unitPrice);
      if (totalCost > 0) {
        rows.push((
          <Fragment key={name}>
            <Box key={`${name}_description`}>
              <Typography variant="h4">{name}</Typography>
              <Typography>{description}</Typography>
            </Box>
            <Box key={`${name}_quantity`} sx={{
              textAlign: "right"
            }}>
              <ISONumber number={usage} />
            </Box>
            <Box key={`${name}_price`} sx={{
              textAlign: "right"
            }}>
              <AbbreviateNumber options={{currency: paygUsage.currency, style: "currency", maximumFractionDigits: 5}} value={unitPrice} />
            </Box>
            <Box key={`${name}_total`} sx={{
              textAlign: "right"
            }}>
              <ISONumber includeDecimals={true} number={totalCost} options={{currency: paygUsage.currency, style: "currency"}} />
            </Box>
          </Fragment>
        ));
      }
    }
  }

  return (
    <Stack
      sx={{
        display: props.visible ? "auto" : "none",
        gap: 1
      }}>
      {isLoading &&
        <TextLoader />
      }
      {!isLoading && !loadError && !paygUsage &&
        <Alert severity="info" variant="outlined">{i18n.t("pay-as-you-go-usage.no-pay-as-you-go-usage-info")}</Alert>
      }
      {!isLoading &&
        <Stack sx={{
          gap: 1
        }}>
          <Box>
            <Typography variant="h3" sx={{
              width: "100%"
            }}>{i18n.t("pay-as-you-go-usage.pay-as-you-go-heading")}</Typography>
            {!loadError &&
              <DateRangeLabel end={props.currentTerm.term_end} start={props.currentTerm.term_start} />
            }
          </Box>
          {loadError &&
            <Alert severity="error" variant="outlined">{loadError}</Alert>
          }
          {!loadError && rows.length === 0 &&
            <Alert severity="info" sx={{marginTop: 1}} variant="outlined">{i18n.t("pay-as-you-go-usage.no-pay-as-you-go-usage-info")}</Alert>
          }
          {!loadError && paygUsage && paygBillingModel && rows.length > 0 &&
            <Stack sx={{
              gap: 1
            }}>
              <Box sx={{
                columnGap: 3,
                display: "grid",
                gridTemplateColumns: "1fr min-content min-content min-content",
                rowGap: 1
              }}>
                <Stack
                  sx={{
                    borderBottom: "1px solid black",
                    gap: 1
                  }}>
                  <Alert color="info" variant="outlined">{i18n.t("pay-as-you-go-usage.pay-as-you-go-details")}</Alert>
                  <DateRangeLabel end={paygUsage.until} start={paygUsage.since} useUTC />
                </Stack>
                <Box
                  sx={{
                    alignSelf: "end",
                    borderBottom: "1px solid black"
                  }}>{i18n.t("pay-as-you-go-usage.usage-quantity")}</Box>
                <Box
                  sx={{
                    alignSelf: "end",
                    borderBottom: "1px solid black"
                  }}>{i18n.t("pay-as-you-go-usage.usage-price")}</Box>
                <Box
                  sx={{
                    alignSelf: "end",
                    borderBottom: "1px solid black"
                  }}>{i18n.t("pay-as-you-go-usage.usage-total")}</Box>
                {rows}
                <Box sx={{
                  justifySelf: "end"
                }}>{i18n.t("pay-as-you-go-usage.usage-subtotal")}</Box>
                <Box
                  sx={{
                    justifySelf: "end",
                    gridColumn: "span 3"
                  }}>
                  <ISONumber includeDecimals={true} number={sumPrice} options={{currency: paygUsage.currency, style: "currency"}} />
                </Box>
              </Box>
            </Stack>
          }
        </Stack>
      }
    </Stack>
  );
}